import React from 'react';
import { Link } from 'react-router-dom';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';

const LOGO_URL = '/logo-meteris.png';

export default function Footer({ onManageCookies }) {
  const { lang } = useLang();
  const f = translations.footer;

  return (
    <footer data-testid="group-footer" className="bg-[#0B0C10] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="MÉTÉRIS" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-white">MÉTÉRIS</span>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              {tx(lang, 'Services informatiques pour entreprises. Votre partenaire IT de confiance.', 'IT services for businesses. Your trusted IT partner.', { es: 'Servicios informáticos para empresas. Tu socio de TI de confianza.', de: 'Computerdienstleistungen für Unternehmen. Ihr vertrauenswürdiger IT-Partner.', it: 'Servizi informatici per le imprese. Il tuo partner IT di fiducia.', pt: 'Serviços informáticos para empresas. O teu parceiro de TI de confiança.' })}
            </p>
            <a href="tel:0183623327" className="inline-flex items-center gap-2 mt-4 text-sm text-slate-300 hover:text-[#0099D6] transition-colors">
              <Phone className="w-4 h-4 text-[#0099D6]" />
              01.83.62.33.27
            </a>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-white mb-5">
              {t(f.services.title, lang)}
            </h4>
            <ul className="space-y-3">
              {f.services.items.map((item, i) => (
                <li key={i}>
                  {item.external ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors flex items-center gap-1">
                      {t(item.label, lang)}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to={item.link} className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors">
                      {t(item.label, lang)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-white mb-5">
              {t(f.resources.title, lang)}
            </h4>
            <ul className="space-y-3">
              {f.resources.items.map((item, i) => (
                <li key={i}>
                  {item.external ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors flex items-center gap-1">
                      {t(item.label, lang)}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to={item.link} className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors">
                      {t(item.label, lang)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Partners Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-white mb-5">
              {t(f.partners.title, lang)}
            </h4>
            <ul className="space-y-3">
              {f.partners.items.map((item, i) => (
                <li key={i}>
                  <Link to={item.link} className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors">
                    {t(item.label, lang)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Ecosystem Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-white mb-5">
              {t(f.ecosystem.title, lang)}
            </h4>
            <ul className="space-y-3">
              {f.ecosystem.items.map((item, i) => (
                <li key={i}>
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-400 hover:text-[#0099D6] transition-colors flex items-center gap-1"
                  >
                    {t(item.label, lang)}
                    {item.link !== '#' && <ExternalLink className="w-3 h-3" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-white mb-5">
              {t(f.contact.title, lang)}
            </h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${f.contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0099D6] transition-colors">
                  <Phone className="w-4 h-4 text-[#0099D6]" />
                  {f.contact.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${f.contact.email}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0099D6] transition-colors">
                  <Mail className="w-4 h-4 text-[#0099D6]" />
                  {f.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-slate-400">
                <MapPin className="w-4 h-4 text-[#0099D6] mt-0.5 flex-shrink-0" />
                {t(f.contact.zone, lang)}
              </li>
            </ul>
            <Link
              to="/prendre-rdv"
              data-testid="footer-cta"
              className="inline-block mt-6 bg-[#0099D6] hover:bg-[#0284C7] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all hover:scale-105"
            >
              {t(translations.common.rdv_cta, lang)}
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Honeypot invisible : Disallow dans robots.txt + CF WAF block. Aucun humain ni crawler GEO ne le visite. Hit = scraper agressif. */}
          <a
            href="/secret-formations-cpf-2026"
            aria-hidden="true"
            tabIndex={-1}
            rel="nofollow noindex"
            style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
          >
            archive
          </a>
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} {t(f.legal.copyright, lang)}
          </p>
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={onManageCookies}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
            >
              {tx(lang, 'G\u00e9rer les cookies', 'Manage cookies', { es: 'Gestionar cookies', de: 'Cookies verwalten', it: 'Gestisci cookie', pt: 'Gerir cookies' })}
            </button>
            <Link to="/cookies" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
              Cookies
            </Link>
            <Link to="/mentions-legales" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
              {t(f.legal.mentions, lang)}
            </Link>
            <Link to="/politique-confidentialite" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
              {t(f.legal.privacy, lang)}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
